import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';

const PendingReviewScreen = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="relative max-w-2xl w-full mx-auto"
      >
        <div className="glass-modal rounded-3xl p-8 sm:p-12 relative overflow-hidden">
          {/* Decorative gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent-400/10 to-transparent pointer-events-none" />
          
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 mx-auto mb-6 glass-accent rounded-full flex items-center justify-center"
          >
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </motion.div>
          
          {/* Main Content */}
          <div className="text-center relative z-10">
            <h2 className={`heading-mono text-2xl sm:text-3xl mb-4 ${
              isDark ? 'text-white' : 'text-gray-800'
            }`}>
              We're Preparing Your Visibility Report
            </h2>
            
            <p className={`text-mono text-base mb-6 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Thank you for completing your onboarding, {user?.firstName || user?.email}!
            </p>
            
            {/* Status Box */}
            <div className={`rounded-xl p-6 mb-8 ${
              isDark ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-start gap-4">
                <div className="glass-spinner w-8 h-8 flex-shrink-0"></div>
                <div className="text-left flex-1">
                  <h3 className={`text-mono font-semibold mb-2 ${
                    isDark ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    What's happening now:
                  </h3>
                  <ul className={`text-mono text-sm space-y-2 ${
                    isDark ? 'text-blue-200' : 'text-blue-600'
                  }`}>
                    <li className="flex items-start gap-2">
                      <span className="text-accent-400 mt-0.5">✓</span>
                      <span>Our team is reviewing and optimizing your search queries</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent-400 mt-0.5">✓</span>
                      <span>Running initial competitor analysis across AI engines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent-400 mt-0.5">✓</span>
                      <span>Preparing your personalized dashboard</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Time Estimate */}
            <div className={`rounded-lg p-4 mb-6 ${
              isDark ? 'bg-yellow-900/20 border border-yellow-700/30' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <p className={`text-mono text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                ⏱️ Estimated time: 2-3 hours
              </p>
              <p className={`text-mono text-xs mt-1 ${isDark ? 'text-yellow-200' : 'text-yellow-600'}`}>
                We'll send you an email as soon as your dashboard is ready
              </p>
            </div>
            
            {/* What to expect */}
            <div className="mb-8">
              <h3 className={`text-mono font-semibold mb-3 ${
                isDark ? 'text-white' : 'text-gray-800'
              }`}>
                What you'll get:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="glass-card p-4 rounded-lg">
                  <div className="text-accent-400 text-2xl mb-2">🎯</div>
                  <p className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    25 optimized search queries
                  </p>
                </div>
                <div className="glass-card p-4 rounded-lg">
                  <div className="text-accent-400 text-2xl mb-2">🤖</div>
                  <p className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Analysis across 4 AI engines
                  </p>
                </div>
                <div className="glass-card p-4 rounded-lg">
                  <div className="text-accent-400 text-2xl mb-2">📊</div>
                  <p className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Competitor visibility rankings
                  </p>
                </div>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <p>Questions? Contact us at</p>
              <a 
                href="mailto:support@eyehill.com" 
                className="text-accent-400 hover:text-accent-300 transition-colors"
              >
                support@eyehill.com
              </a>
            </div>
            
            {/* Logout Button */}
            <div className="mt-8">
              <button
                onClick={() => {
                  // Remove auth-related items but preserve theme
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  localStorage.removeItem('onboardingProgress');
                  localStorage.removeItem('searchExecutionState');
                  window.location.href = '/';
                }}
                className={`text-mono text-sm underline transition-colors ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PendingReviewScreen;